import { useMemo, useRef } from 'react';
import { formatROI } from '@shared';
import { createShortTermCareQuoteWithROIandProjectedCoverage } from '@/util/policyQuoteCalculations';

export interface ShortTermCareQuoteProjection {
  projectedCostCoverage: number;
  /** Numeric ROI for the client's chosen calculation type — the sort key. */
  roi: number;
  formattedROI?: string;
}

interface UseShortTermCareQuoteProjectionsParams {
  quotes: ShortTermCareQuote[];
  client: Client | undefined;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
}

/**
 * Projected LTC coverage and ROI for the short-term care quotes on screen.
 *
 * Each projection is a full client-derivative pass, so this is the most
 * expensive thing the view does — and the reason it is scoped to the FILTERED
 * quotes rather than everything fetched. Results are cached across renders by
 * quote id and rate vintage: filtering, expanding a card and selecting a policy
 * all re-render the list, and none of them change what a given quote projects.
 *
 * A ref cache rather than React Query state: the computation is synchronous and
 * local, so there is no request to dedupe, only arithmetic to avoid repeating.
 */
export function useShortTermCareQuoteProjections({
  quotes,
  client,
  returnOnInvestmentCalculationType,
}: UseShortTermCareQuoteProjectionsParams): Record<string, ShortTermCareQuoteProjection> {
  const cache = useRef<Map<string, ShortTermCareQuoteProjection> | null>(null);
  cache.current ??= new Map();

  /**
   * A projection depends on the whole client — care phases, care costs, birth
   * date, assets, discount settings — not just its id. Redux replaces the
   * client object immutably on every edit, so identity is the correct
   * invalidation signal: keying on clientId alone would keep serving pre-edit
   * figures after an assumptions change.
   */
  const cacheScope = { client, returnOnInvestmentCalculationType };
  const previousScope = useRef(cacheScope);
  if (
    previousScope.current.client !== client ||
    previousScope.current.returnOnInvestmentCalculationType !== returnOnInvestmentCalculationType
  ) {
    cache.current = new Map();
    previousScope.current = cacheScope;
  }

  return useMemo(() => {
    if (!client) return {};

    const projections: Record<string, ShortTermCareQuoteProjection> = {};

    const projectionCache = (cache.current ??= new Map());

    for (const quote of quotes) {
      const cacheKey = `${quote.shortTermCareQuoteId}|${quote.ratesAsOf}`;
      const cached = projectionCache.get(cacheKey);

      if (cached) {
        projections[quote.shortTermCareQuoteId] = cached;
        continue;
      }

      try {
        const enriched = createShortTermCareQuoteWithROIandProjectedCoverage(
          quote,
          client,
          returnOnInvestmentCalculationType,
        );
        const projection: ShortTermCareQuoteProjection = {
          projectedCostCoverage: enriched.projectedCostCoverage,
          roi: enriched.roi,
          formattedROI: formatROI(enriched.roiValues, returnOnInvestmentCalculationType),
        };

        projectionCache.set(cacheKey, projection);
        projections[quote.shortTermCareQuoteId] = projection;
      } catch (error) {
        // One bad quote must not blank the whole list; the card simply renders
        // without a coverage figure.
        console.error(
          'Error calculating projected coverage for short-term care quote:',
          quote.shortTermCareQuoteId,
          error,
        );
      }
    }

    return projections;
  }, [quotes, client, returnOnInvestmentCalculationType]);
}
